import { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useTranslation } from "react-i18next";
import { img } from "../constants/assets";
import { createAppointment } from "../services/apiService";

const getAvailableDate = (value) => {
  const date = new Date();

  switch (value) {
    case "tomorrow":
      date.setDate(date.getDate() + 1);
      break;
    case "this-week":
      date.setDate(date.getDate() + 3);
      break;
    case "next-week":
      date.setDate(date.getDate() + 7);
      break;
    default:
      break;
  }

  date.setHours(10, 0, 0, 0);
  return date.toISOString();
};

function BookDemoModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  useEffect(() => {
    if (!isOpen) {
      setPhone("");
      setStatus({ type: null, message: "" });
      setIsSubmitting(false);
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: "" });

    const formData = new FormData(event.target);
    const fullPhone = phone.replace(/\s/g, "");
    const extractedCountryCode =
      "+" + (fullPhone.match(/^\d{1,4}/)?.[0] || "971");
    const extractedPhoneNumber = fullPhone.replace(/^\d{1,4}/, "");

    const payload = {
      fullName: formData.get("fullName")?.trim(),
      email: formData.get("email")?.trim(),
      countryCode: extractedCountryCode,
      phoneNumber: extractedPhoneNumber,
      date: getAvailableDate(formData.get("date")),
      message: formData.get("message")?.trim(),
    };

    setIsSubmitting(true);

    try {
      await createAppointment(payload);
      event.target.reset();
      setPhone("");
      setStatus({
        type: "success",
        message: t("bookDemo.success"),
      });
      setTimeout(onClose, 2000);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          t("bookDemo.error"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="book-demo-overlay" onClick={onClose} role="presentation">
      <div
        className="book-demo-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-demo-title"
      >
        <button
          type="button"
          className="book-demo-close"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          &times;
        </button>
        <div className="book-demo-content-wrap">
          <div className="book-demo-image">
            <img src={img("bookdemoimg.svg")} alt="" aria-hidden="true" />
          </div>

          <div className="book-demo-content">
            <h2 id="book-demo-title" className="book-demo-title">
              {t("bookDemo.title")}
            </h2>
            <p className="book-demo-subtitle">{t("bookDemo.subtitle")}</p>

            <form className="book-demo-form" onSubmit={handleSubmit}>
              <div className="book-demo-form-grid">
                <div className="book-demo-form-col">
                  <div className="book-demo-field">
                    <label htmlFor="book-demo-name">{t("common.fullName")}</label>
                    <input
                      id="book-demo-name"
                      name="fullName"
                      type="text"
                      placeholder={t("bookDemo.fullNamePlaceholder")}
                      required
                    />
                  </div>

                  <div className="book-demo-field">
                    <label htmlFor="book-demo-email">{t("common.email")}</label>
                    <input
                      id="book-demo-email"
                      name="email"
                      type="email"
                      placeholder={t("bookDemo.emailPlaceholder")}
                      required
                    />
                  </div>

                  <div className="book-demo-field">
                    <label htmlFor="book-demo-phone">
                      {t("common.phoneNumber")}
                    </label>
                    <PhoneInput
                      country="ae"
                      value={phone}
                      onChange={setPhone}
                      inputProps={{
                        id: "book-demo-phone",
                        name: "phoneNumber",
                        required: true,
                      }}
                      inputClass="book-demo-phone-input"
                      containerClass="phone-input-container"
                      buttonClass="country-dropdown-button"
                      dropdownClass="country-dropdown"
                      searchClass="country-search"
                      enableSearch
                      disableSearchIcon
                      searchPlaceholder={t("common.searchCountry")}
                      placeholder={t("common.enterPhone")}
                    />
                  </div>
                </div>

                <div className="book-demo-form-col">
                  <div className="book-demo-field">
                    <label htmlFor="book-demo-date">
                      {t("bookDemo.availableDate")}
                    </label>
                    <select id="book-demo-date" name="date" defaultValue="" required>
                      <option value="" disabled>
                        {t("common.select")}
                      </option>
                      <option value="today">{t("bookDemo.today")}</option>
                      <option value="tomorrow">{t("bookDemo.tomorrow")}</option>
                      <option value="this-week">{t("bookDemo.thisWeek")}</option>
                      <option value="next-week">{t("bookDemo.nextWeek")}</option>
                    </select>
                  </div>

                  <div className="book-demo-field book-demo-field--message">
                    <label htmlFor="book-demo-message">
                      {t("bookDemo.shareMessage")}
                    </label>
                    <textarea
                      id="book-demo-message"
                      name="message"
                      placeholder={t("bookDemo.messagePlaceholder")}
                      rows={5}
                      required
                    />
                  </div>
                </div>
              </div>

              {status.message && (
                <p
                  className={`contact-form-status contact-form-status--${status.type}`}
                  role="alert"
                >
                  {status.message}
                </p>
              )}

              <div className="book-demo-actions">
                <button
                  type="submit"
                  className="book-demo-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("common.booking") : t("common.bookNow")}
                  <img src={img("arrow.svg")} alt="" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookDemoModal;
